"use client";

import { IconArrowCircleUp, IconClockCountdown, IconShieldCheck } from "@decibeltrade/react-icons";
import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { trialDuration } from "./trial-duration";

export function DetailsHero({ trialMinutes }: { trialMinutes: number }) {
  const t = useTranslations("firstTradeOnUs");
  const duration = trialDuration(trialMinutes);

  return (
    <div className={containerClass} data-testid="fft-details-hero">
      <h2 className={titleClass}>{t("hero.title")}</h2>
      <p className={taglineClass}>{t("tagline")}</p>
      <div className={chipsRowClass}>
        <Chip
          icon={<IconArrowCircleUp className={iconClass} />}
          label={t("hero.chips.profitsKeep")}
        />
        <Chip
          icon={<IconShieldCheck className={iconClass} />}
          label={t("hero.chips.zeroDownside")}
        />
        <Chip
          icon={<IconClockCountdown className={iconClass} />}
          label={
            duration.unit === "hour"
              ? t("hero.chips.durationHours", { hours: duration.value })
              : t("hero.chips.duration", { minutes: duration.value })
          }
        />
      </div>
      <p className={helperClass}>{t("hero.helper")}</p>
    </div>
  );
}

interface ChipProps {
  icon: React.ReactNode;
  label: string;
}

function Chip({ icon, label }: ChipProps) {
  return (
    <div className={chipClass} data-testid="fft-details-hero-chip">
      {icon}
      <span className={chipLabelClass}>{label}</span>
    </div>
  );
}

const iconClass = css({ w: "20", h: "20", color: "icon.secondary" });

const containerClass = stack({
  align: "center",
  justify: "center",
  gap: "24",
  py: "48",
  px: "16",
  w: "full",
  flex: "1",
});

const titleClass = css({
  textStyle: "subheading.md",
  color: "text.primary",
  textAlign: "center",
});

const taglineClass = css({
  textStyle: "body.md",
  color: "text.tertiary",
  textAlign: "center",
  maxW: "[390px]",
});

const chipsRowClass = flex({
  gap: "12",
  wrap: "wrap",
  justify: "center",
});

const helperClass = css({
  textStyle: "body.md",
  color: "text.disabled",
  textAlign: "center",
});

const chipClass = flex({
  align: "center",
  gap: "8",
  px: "12",
  py: "8",
  borderStyle: "solid",
  borderWidth: "100",
  borderColor: "border.primary",
  rounded: "full",
});

const chipLabelClass = css({ textStyle: "body.md", color: "text.secondary" });
